package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.Placeholders
import dev.lingvo.shared.pluralCategoriesFor
import dev.lingvo.shared.textDirection
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCheckIcon
import dev.lingvo.site.ds.LDiffText
import dev.lingvo.site.ds.LKbd
import dev.lingvo.site.ds.LWarningIcon
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import kotlin.math.roundToInt
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun SuggestionAssistItem(
    state: TranslationEditorState,
    row: KeyRow,
    result: EditorAssistResult.Suggestion,
    onSuggestionInserted: (EditorSuggestionInsertion) -> Unit,
) {
    val item = result.suggestion
    val ordinal = result.shortcutNumber
    val safe = item.warning == null
    val provenance = when (item) {
        is EditorSuggestion.Machine -> item.provider.displayName
        is EditorSuggestion.TranslationMemory ->
            t("editor.editor_assist_suggestion_result.translation_memory", "Translation memory")
    }
    Li(
        Modifier
            .attr("data-editor-assist-result", "true")
            .attr("data-editor-assist-ordinal", ordinal.toString())
            .attr("data-editor-ranked-kind", item.kind)
            .toAttrs(),
    ) {
        Div(Modifier.attr("data-editor-assist-result-header", "true").toAttrs()) {
            AssistOrdinal(ordinal)
            Span(Modifier.attr("data-editor-assist-provenance", item.kind).toAttrs()) {
                Text(provenance)
            }
            when (item) {
                is EditorSuggestion.Machine -> {
                    LBadge(t("editor.editor_assist_suggestion_result.machine", "Machine"), variant = LBadgeVariant.ACCENT)
                }
                is EditorSuggestion.TranslationMemory -> {
                    LBadge(
                        tf(
                            "editor.editor_assist_suggestion_result.percent_match",
                            "{percent}% match",
                            "percent" to similarityPercent(item.similarity).toString(),
                        ),
                        variant = LBadgeVariant.INFO,
                    )
                }
            }
            LBadge(
                assistSuggestionStatus(item),
                modifier = Modifier.attr("data-editor-assist-status", "true"),
                variant = if (safe) LBadgeVariant.NEUTRAL else LBadgeVariant.WARNING,
            )
            if (item is EditorSuggestion.TranslationMemory) {
                Span(Modifier.attr("data-editor-assist-source-key", "true").toAttrs()) {
                    Text(item.sourceKeyName)
                }
            }
        }
        SuggestionValue(row, state.locale, item)
        if (item is EditorSuggestion.TranslationMemory && item.matchedSource != null) {
            Div(
                Modifier
                    .attr("data-editor-assist-evidence", "matched-source")
                    .toAttrs(),
            ) {
                Text(t("editor.editor_assist_suggestion_result.matched_source", "Matched source: "))
                Span(
                    Modifier
                        .attr("data-editor-matched-source", "true")
                        .attr("lang", state.project?.sourceLocale.orEmpty())
                        .attr("dir", textDirection(state.project?.sourceLocale).html)
                        .toAttrs(),
                ) {
                    LDiffText(row.sourceText(), item.matchedSource)
                }
            }
        }
        item.warning?.let { warning ->
            Row(
                Modifier
                    .fillMaxWidth()
                    .gap(Space.SM)
                    .attr("data-editor-assist-evidence", "warning"),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                LWarningIcon(Modifier.iconSize(IconSize.MD))
                Span(Modifier.toAttrs()) { Text(warning) }
            }
        }
        if (state.capability.canWriteTranslations) {
            val insertLabel = if (safe) {
                t("editor.editor_assist_suggestion_result.insert", "Insert")
            } else {
                t("editor.editor_assist_suggestion_result.insert_anyway", "Insert anyway")
            }
            // Substituted here rather than at the call site below, because two chained `replace`
            // calls read each other's output: a provenance containing `{ordinal}` would have its
            // own text rewritten. `tf` scans the message once.
            val insertAnnouncement = if (safe) {
                tf(
                    "editor.editor_assist_suggestion_result.insert_suggestion_from",
                    "Insert suggestion {ordinal} from {provenance}",
                    "ordinal" to ordinal,
                    "provenance" to provenance,
                )
            } else {
                tf(
                    "editor.editor_assist_suggestion_result.review_and_insert_suggestion_from",
                    "Review and insert suggestion {ordinal} from {provenance}",
                    "ordinal" to ordinal,
                    "provenance" to provenance,
                )
            }
            Div(Modifier.attr("data-editor-assist-actions", "true").toAttrs()) {
                LKbd("Alt+$ordinal")
                LButton(
                    label = insertLabel,
                    onClick = {
                        notifySuggestionInsertion(
                            state.insertSuggestion(item),
                            onSuggestionInserted,
                        )
                    },
                    modifier = Modifier
                        .editorAssistWrappingAction()
                        .attr("id", assistResultActionId(ordinal, "insert"))
                        .attr(EDITOR_ASSIST_RESULT_IDENTITY_ATTRIBUTE, result.actionIdentity()),
                    // **The safe one is the offer; the unsafe one is a decision.**
                    //
                    // These were the wrong way round: `GHOST` on a clean 94% match and `SECONDARY`
                    // on the suggestion that drops a placeholder, which drew the recessive
                    // treatment on the thing worth taking and the fuller one on the thing worth
                    // hesitating over. A safe insertion is the panel's whole purpose, so it gets
                    // the weight and a check; "Insert anyway" keeps the word "anyway" and the
                    // warning glyph and stays the quietest button in the row.
                    variant = if (safe) LButtonVariant.SECONDARY else LButtonVariant.GHOST,
                    size = LButtonSize.SM,
                    leadingIcon = { if (safe) LCheckIcon() else LWarningIcon() },
                    accessibleLabel = insertAnnouncement,
                )
            }
        }
    }
}

@Composable
private fun SuggestionValue(row: KeyRow, locale: String, item: EditorSuggestion) {
    val palette = ColorMode.current.toSitePalette()
    Column(
        EditorAssistSuggestionValueStyle.toModifier()
            .fillMaxWidth()
            .gap(Space.SM)
            .attr("data-editor-assist-value", "true")
            .attr("data-editor-suggestion-value", "true")
            .attr("lang", locale)
            .attr("dir", textDirection(locale).html),
    ) {
        if (row.key.isPlural) {
            pluralCategoriesFor(locale).forEach { category ->
                val value = item.pluralForms?.get(category).orEmpty()
                Column(Modifier.fillMaxWidth().gap(Space.XXS)) {
                    Span(
                        Modifier
                            .fontSize(TypeScale.Micro)
                            .fontWeight(700)
                            .color(palette.textMuted)
                            .toAttrs(),
                    ) {
                        Text(category.displayName())
                    }
                    MarkInvalidPlaceholders(row.sourceFor(category), value)
                }
            }
        } else {
            MarkInvalidPlaceholders(row.sourceText(), item.value.orEmpty())
        }
    }
}

@Composable
private fun MarkInvalidPlaceholders(source: String, value: String) {
    val invalid = Placeholders.validate(source, value)
        .map { it.placeholder }
        .filter(String::isNotBlank)
        .toSet()
    val empty = t("editor.editor_assist_suggestion_result.no_text_returned", "No text returned")
    MarkedText(value.ifBlank { empty }, invalid)
}

@Composable
private fun MarkedText(value: String, marked: Set<String>) {
    val palette = ColorMode.current.toSitePalette()
    if (marked.isEmpty()) return Text(value)
    val expression = Regex(marked.sortedByDescending(String::length).joinToString("|") { Regex.escape(it) })
    var cursor = 0
    expression.findAll(value).forEach { match ->
        if (match.range.first > cursor) Text(value.substring(cursor, match.range.first))
        Span(
            Modifier
                .color(palette.danger)
                .fontWeight(700)
                .styleModifier {
                    property("text-decoration", "underline")
                    property("text-decoration-thickness", "2px")
                }
                .toAttrs(),
        ) {
            Text(match.value)
        }
        cursor = match.range.last + 1
    }
    if (cursor < value.length) Text(value.substring(cursor))
}

private val EditorSuggestion.kind: String
    get() = when (this) {
        is EditorSuggestion.Machine -> "machine"
        is EditorSuggestion.TranslationMemory -> "translation-memory"
    }

private fun similarityPercent(similarity: Double): Int =
    (if (similarity <= 1.0) similarity * 100 else similarity).roundToInt().coerceIn(0, 100)
